import {
  Button,
  Collapse,
  InputAmount,
  InputDay,
  InputPercentage,
  InputSelect,
  InputWrapper,
  type SelectItem,
} from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateHasuraQueries,
  useQuery,
  useTrackingEvent,
  useTeam,
} from '@vrplatform/data';
import {
  AlertTriangleIcon,
  Icon,
  PlusIcon,
  TrashIcon,
} from '@vrplatform/icons';
import { IconButton, LoadingIndicator } from '@vrplatform/components-legacy';
import {
  type Maybe,
  bankersRound,
  day,
  ensure,
  hasValue,
  sum,
  utc,
} from '@vrplatform/utils';
import {
  Alert,
  Box,
  Checkbox,
  Group,
  Collapse as MantineCollapse,
  Stack,
  Text,
  rem,
} from '@mantine/core';
import type { listingOwnershipPeriodBusinessModel_enum } from '@vrplatform/graphql';
import {
  formatOwnerName,
  getCurrencySymbol,
  formatOwnershipBusinessModel,
  orderByContact,
  whereContacts,
} from '@vrplatform/ui-common';
import { useCallback, useMemo } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { getListingPeriodRequestBody } from './ListingPeriodForm.utils';

type FormInputs = {
  id: Maybe<string>;
  startDate: Maybe<string>;
  businessModel?: listingOwnershipPeriodBusinessModel_enum;
  endDate: Maybe<string>;
  reserveCentTotal: number | '';
  members: {
    ownerId: string | null;
    splitBasePoints: number;
  }[];
};

function useListingPeriodMutation(listingId: string) {
  const inv = useInvalidateHasuraQueries();

  const invalidate = () => {
    inv(['listings', 'listingOwners']);
    inv(['issues']);
  };

  const track = useTrackingEvent();

  const { mutateAsync: insert } = useApiMutation(
    'post',
    '/listings/ownership-periods',
    {
      onSettled: () => {
        invalidate();
      },
    }
  );

  const { mutateAsync: update } = useApiMutation(
    'put',
    '/listings/ownership-periods/{id}',
    {
      onSettled: () => {
        invalidate();
      },
    }
  );

  const mutate = useCallback(
    async (values: FormInputs) => {
      const requestBody = getListingPeriodRequestBody({
        listingId,
        startDate: values.startDate,
        endDate: values.endDate,
        businessModel: values.businessModel,
        reserveCentTotal:
          values.reserveCentTotal === '' ? 0 : values.reserveCentTotal,
        members: values.members,
      });

      if (values.id) {
        await update({
          params: {
            path: {
              id: values.id,
            },
          },
          body: {
            ...requestBody,
            onConflict: 'updateExisting',
          },
        });
      } else {
        await insert({
          body: {
            ...requestBody,
            businessModel: values.businessModel ?? 'managed',
            onConflict: 'updateExisting',
          },
        }).then((res) => {
          track('ownership_period_created', {
            listingId,
          });

          return res;
        });
      }
    },
    [listingId, insert, update, track]
  );

  return {
    mutate,
  };
}

const BUSINESS_MODELS: SelectItem<listingOwnershipPeriodBusinessModel_enum>[] =
  [
    {
      label: formatOwnershipBusinessModel('managed'),
      value: 'managed',
    },
    {
      label: formatOwnershipBusinessModel('co_host'),
      value: 'co_host',
    },
  ];

export const ListingPeriodForm = ({
  initialValues,
  onReset,
  loadingQuery,
  previousValues,
  listingId,
}: {
  onReset: () => void;
  initialValues: FormInputs | undefined;
  previousValues:
    | { id: string; startAt: Maybe<string>; endAt: Maybe<string> }
    | undefined;
  loadingQuery: boolean;
  listingId: string;
}) => {
  const [{ currency }] = useTeam();

  const currencySymbol = useMemo(
    () => getCurrencySymbol(currency).replaceAll(/[A-Za-z]/g, ''),
    [currency]
  );

  const methods = useForm<FormInputs>({
    values: initialValues
      ? {
          id: initialValues.id,
          endDate: initialValues.endDate
            ? utc(initialValues.endDate).yyyymmdd()
            : undefined,
          startDate: initialValues.startDate
            ? utc(initialValues.startDate).yyyymmdd()
            : day().yyyymmdd(),
          reserveCentTotal: initialValues.reserveCentTotal ?? 0,
          members: initialValues.members,
          businessModel: initialValues.businessModel ?? 'managed',
        }
      : {
          id: undefined,
          endDate: null,
          startDate: day().yyyymmdd(),
          reserveCentTotal: 0,
          businessModel: 'managed',
          members: [
            {
              ownerId: null,
              splitBasePoints: 100000,
            },
          ],
        },
  });

  const { mutate } = useListingPeriodMutation(listingId);

  const handleSubmit = useCallback(
    async (input: FormInputs) => {
      try {
        await mutate(input).then(onReset);
      } catch (error: any) {
        showApiErrorNotification({
          title: 'Failed to save ownership',
          error,
        });
      }
    },
    [onReset, mutate]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const previousEndDate = useMemo(() => {
    if (previousValues) {
      if (previousValues.endAt) {
        const res = utc(previousValues.endAt).yyyymmdd();
        return day(res).toDate();
      }
      const res = utc(previousValues.startAt).yyyymmdd();
      return day(res).add(1, 'day').toDate();
    }

    return undefined;
  }, [previousValues?.startAt, previousValues?.endAt]);

  if (loadingQuery) return <LoadingIndicator isFullPageLoading size="sm" />;

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        sx={() => ({
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        })}
        onSubmit={methods.handleSubmit(handleSubmit)}
        onReset={onReset}
      >
        <Stack>
          <Box>
            <Controller
              control={methods.control}
              defaultValue={day().yyyymmdd()}
              name="startDate"
              render={({ field, fieldState: { error } }) => (
                <InputWrapper
                  label="Ownership start"
                  error={error?.message}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                >
                  <InputDay
                    value={field.value ? day(field.value).toDate() : null}
                    onChange={(value) => field.onChange(day(value).yyyymmdd())}
                    placeholder={day().yyyymmdd()}
                    error={!!error}
                    minDate={previousEndDate}
                  />
                </InputWrapper>
              )}
            />
          </Box>
          <Box>
            <Controller
              control={methods.control}
              name="endDate"
              render={({ field, fieldState: { error } }) => (
                <InputWrapper
                  label={
                    <>
                      Ownership end{' '}
                      <Text component="span" sx={{ fontWeight: 400 }}>
                        (start of next ownership)
                      </Text>
                    </>
                  }
                  error={error?.message}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                >
                  <InputDay
                    value={field.value ? day(field.value).toDate() : null}
                    onChange={(value) =>
                      field.onChange(value ? day(value).yyyymmdd() : null)
                    }
                    minDate={
                      !initialValues
                        ? undefined
                        : day(initialValues.startDate).add(1, 'day').toDate()
                    }
                    placeholder={
                      !initialValues
                        ? 'Forever'
                        : day(initialValues.startDate).add(1, 'day').yyyymmdd()
                    }
                    error={!!error}
                    disabled={!initialValues}
                    clearable
                  />
                </InputWrapper>
              )}
            />
          </Box>

          <Box>
            <Controller
              control={methods.control}
              name="businessModel"
              render={({ field, fieldState: { error } }) => {
                const value =
                  BUSINESS_MODELS.find(
                    (x) =>
                      x.value ===
                      (field.value === 'co_host_airbnb'
                        ? ensure<listingOwnershipPeriodBusinessModel_enum>(
                            'co_host'
                          )
                        : field.value)
                  ) ?? null;

                // const disabled = !!initialValues?.id;
                const disabled = false;

                return (
                  <InputWrapper
                    label="Business model"
                    error={error?.message}
                    inputWrapperOrder={[
                      'label',
                      'input',
                      'description',
                      'error',
                    ]}
                  >
                    <InputSelect
                      type="single"
                      setValue={(v) => v?.value && field.onChange(v.value)}
                      value={value}
                      data={{
                        options: BUSINESS_MODELS,
                      }}
                      dropdownProps={{
                        width: 'target',
                      }}
                      inputProps={{
                        disabled,
                      }}
                    />
                    <MantineCollapse in={!!field.value?.includes('co_host')}>
                      <Checkbox
                        mt="sm"
                        disabled={disabled}
                        ml={rem(2)}
                        checked={field.value === 'co_host_airbnb'}
                        onChange={(event) =>
                          field.onChange(
                            ensure<listingOwnershipPeriodBusinessModel_enum>(
                              event.currentTarget.checked
                                ? 'co_host_airbnb'
                                : 'co_host'
                            )
                          )
                        }
                        label="Apply Co-host model to Airbnb booking channel reservations only"
                      />
                    </MantineCollapse>
                  </InputWrapper>
                );
              }}
            />
          </Box>

          <Box mb="xl">
            <Controller
              control={methods.control}
              name="reserveCentTotal"
              rules={{
                validate: (value) => {
                  const v = value || 0;

                  if (v < 0) {
                    return 'Please input a positive number';
                  }

                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <InputWrapper
                  label="Owner reserve"
                  error={error?.message}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                >
                  <InputAmount
                    value={
                      typeof field.value === 'number' ? field.value / 100 : ''
                    }
                    setValue={(value) =>
                      field.onChange(
                        value === '' ? '' : bankersRound(Number(value) * 100, 0)
                      )
                    }
                    debounce={0}
                    decimalScale={2}
                    error={!!error}
                    placeholder="0.00"
                    leftSection={<span>{currencySymbol}</span>}
                    hideControls
                  />
                </InputWrapper>
              )}
            />
          </Box>

          <Box>
            <Owners />
          </Box>
        </Stack>

        <Group
          mt="md"
          sx={(theme) => ({
            position: 'sticky',
            bottom: 0,
            height: 60,
            left: 0,
            right: 0,
            paddingBottom: theme.spacing.md,
            backgroundColor: theme.white,
            borderTop: `1px solid ${theme.colors.gray[2]}`,
            paddingTop: theme.spacing.sm,
            paddingInline: theme.spacing.xs,
            marginRight: `-${theme.spacing.xs}`,
            marginLeft: `-${theme.spacing.xs}`,
          })}
        >
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            disabled={!methods.formState.isDirty && !!initialValues}
            loading={methods.formState.isSubmitting}
            sx={{ flexGrow: '1!important' }}
            type="submit"
            variant="primary"
          >
            Save changes
          </Button>
        </Group>
      </Box>
    </FormProvider>
  );
};

const Owners = () => {
  const { control, getValues, watch, formState } = useFormContext<FormInputs>();
  const [{ id: teamId }] = useTeam();

  const hasOwners = useQuery(
    (q, { teamId }) => {
      const hasOwners = !!q.contacts({
        limit: 1,
        where: whereContacts({
          tenantId: teamId,
          search: undefined,
          type: 'owner',
        }),
      })[0]?.id;

      return {
        hasOwners,
      };
    },
    {
      skip: !teamId,
      queryKey: 'owners',
      keepPreviousData: true,
      variables: {
        teamId,
      },
    }
  );

  const { append, fields, remove } = useFieldArray({
    name: 'members',
    control,
    rules: {
      minLength: 1,
      required: true,
      validate: (value) => {
        const total = bankersRound(sum(value, 'splitBasePoints') / 1000);
        if (total !== 100) {
          return 'All splits must sum to exactly 100%.';
        }
        return true;
      },
    },
  });

  const hasNoOwners = !hasOwners.data?.hasOwners;
  const rows = watch('members');

  return (
    <Collapse
      hideToggle
      title="Owners"
      rightSection={
        <IconButton
          disabled={hasNoOwners}
          onClick={() => {
            const currentSplitTotal = bankersRound(
              sum(getValues('members'), 'splitBasePoints') / 100
            );

            append({
              ownerId: null,
              splitBasePoints:
                currentSplitTotal < 100 && currentSplitTotal >= 0
                  ? 100 - currentSplitTotal
                  : 0,
            });
          }}
        >
          <PlusIcon />
        </IconButton>
      }
    >
      <Stack
        sx={(theme) => ({
          gap: theme.spacing.xs,
        })}
      >
        {fields.map((access, index) => {
          return (
            <Group key={access.id} wrap="nowrap" gap="xs" align="flex-start">
              <Controller
                control={control}
                name={`members.${index}.ownerId`}
                rules={{
                  required:
                    index === 0
                      ? 'Select at least one owner'
                      : 'Please select an owner',
                }}
                render={({ field, fieldState: { error } }) => {
                  const alreadySelectedOwnerIds = rows
                    .filter((x) => x.ownerId !== field.value)
                    .map((x) => x.ownerId)
                    .filter(hasValue);

                  const queryData = useQuery(
                    (q, { teamId }) => {
                      return q
                        .contacts({
                          where: whereContacts({
                            tenantId: teamId,
                            type: 'owner',
                          }),
                          order_by: orderByContact,
                        })
                        .map<SelectItem>((contact) => ({
                          label: formatOwnerName(contact) || '',
                          value: contact.id,
                          icon: (
                            <Icon
                              icon={
                                !contact.companyType ? 'UserIcon' : 'OfficeIcon'
                              }
                              size={contact.companyType ? 16 : 14}
                            />
                          ),
                        }));
                    },
                    {
                      skip: !teamId,
                      queryKey: 'owners',
                      variables: {
                        teamId,
                      },
                      keepPreviousData: true,
                    }
                  );

                  const options = (queryData.data || []).filter(
                    (v) => !alreadySelectedOwnerIds.includes(v.value)
                  );
                  const value =
                    options.find((x) => x.value === field.value) || null;

                  return (
                    <InputWrapper required error={error?.message} flex={1}>
                      <InputSelect
                        data={{
                          options,
                          loading: queryData.isLoading,
                          error: queryData.error,
                          sort: null,
                        }}
                        type="single"
                        value={value}
                        setValue={(newValue) => {
                          field.onChange(newValue?.value || null);
                        }}
                        dropdownProps={{
                          withinPortal: true,
                        }}
                        inputProps={{
                          placeholder: 'Owner',
                          error: !!error,
                          loadingQuery: queryData.isInitialLoading,
                          lineClamp: 1,
                        }}
                      />
                    </InputWrapper>
                  );
                }}
              />
              <Controller
                control={control}
                name={`members.${index}.splitBasePoints`}
                rules={{
                  required: 'Split is required',
                  min: {
                    value: 0.01,
                    message: 'Split must be greater than 0%',
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <InputWrapper required error={error?.message} maw={100}>
                      <InputPercentage
                        placeholder="0.00"
                        value={
                          typeof field.value === 'number'
                            ? field.value / 1000
                            : ''
                        }
                        onChange={(value) => {
                          const v =
                            value === ''
                              ? null
                              : bankersRound(Number(value) * 1000);

                          field.onChange(v);
                        }}
                        min={0}
                        max={100}
                        decimalScale={3}
                        error={!!error}
                        hideControls
                      />
                    </InputWrapper>
                  );
                }}
              />
              <Box w={30}>
                {!!index && (
                  <IconButton onClick={() => remove(index)} pt={5}>
                    <TrashIcon color="gray" size={16} />
                  </IconButton>
                )}
              </Box>
            </Group>
          );
        })}
        {formState.errors.members?.root?.message && (
          <Alert
            icon={<AlertTriangleIcon color="red" size={16} />}
            title="Failed to validate"
            color="red"
          >
            {formState.errors.members.root?.message}
          </Alert>
        )}
      </Stack>
    </Collapse>
  );
};
